import { ArrowRightIcon } from "Icons/ArrowRight";
import { SearchIcon } from "Icons/Search";

import { useEffect, useState } from "react";

import { css } from "@emotion/react";

import { ButtonWrapper } from "components/shared/library/ButtonWrapper";
import { Divider } from "components/shared/library/Divider";
import { InputSelect } from "components/shared/library/InputSelect";
import { OptionType } from "components/shared/library/OptionsMenu";
import { Typography } from "components/shared/library/Typography";

import {
  Location,
  fetchLocationByPlaceId,
  fetchLocationPredictions,
} from "apis/userLocation";

import { uiGray } from "constants/colors";

const style = {
  optionComponent: css({ display: "flex", alignItems: "center", gap: 8 }),
  footer: css({ padding: 10 }),
  divider: css({ margin: "0 0 10px 0" }),
};

type Props = {
  location?: Location;
  onSelect: (value: Location) => void;
  displayLabel?: boolean;
  hideFooter?: boolean;
  showPlaceholder?: boolean;
};

export const LocationSelectionInput = ({
  location,
  onSelect,
  displayLabel,
  hideFooter = false,
  showPlaceholder = false,
}: Props) => {
  const [locationOptions, setLocationOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    if (location) {
      const get = async () => {
        const options = await fetchLocationPredictions(location?.name);
        setLocationOptions(options);
      };
      get();
    }
  }, [location]);

  return (
    <InputSelect
      customCss={{ wrapper: css({ flex: 1 }) }}
      selectedValue={location?.name}
      SuffixComponent={
        <SearchIcon width={16} height={16} stroke={uiGray[70]} />
      }
      placeholder={showPlaceholder ? "Start typing" : undefined}
      displayLabel={displayLabel}
      label={"Location"}
      onSelect={(value) => {
        const get = async () => {
          const { name, lat, lng } = await fetchLocationByPlaceId(value);
          onSelect({ name, lat, lng });
        };
        get();
      }}
      onChange={(value) => {
        const get = async () => {
          const options = await fetchLocationPredictions(value);
          setLocationOptions(options);
        };
        if (value) {
          get();
        } else {
          setLocationOptions([]);
        }
        return value;
      }}
      options={locationOptions || []}
      OptionFooter={
        hideFooter
          ? undefined
          : ({ searchText, onClick }) =>
              !searchText ? (
                <></>
              ) : (
                <div css={style.footer}>
                  <Divider customCss={style.divider} />
                  <ButtonWrapper
                    onClick={async () => {
                      if (!searchText) return;
                      onSelect({ name: searchText });
                      onClick();
                    }}
                    disabled={!searchText || !locationOptions}
                    customCss={style.optionComponent}
                  >
                    <ArrowRightIcon width={16} height={16} />
                    <Typography size="S" weight="bold">
                      {searchText}
                    </Typography>
                    <Typography size="XS" weight="regular">
                      Create custom location
                    </Typography>
                  </ButtonWrapper>
                </div>
              )
      }
      setSearchTextOnSelect
      enableFooterSelectOnClickEnter
      closeOnSelect
    />
  );
};
